import {
  Button, DataProvider, useGetManyAggregate, useRecordContext,
} from 'react-admin';
import {
  fetchRelatedRecords, useRedirect, useTranslate,
} from 'ra-core';
import {
  Box,
  Dialog, DialogActions, DialogTitle, IconButton,
  Button as MuiButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import { useMemo, useState } from 'react';
import { get } from 'lodash';
import { Asset } from '@x-guard/xgac-types/xgac';
import { AssetTableInDialog } from '../AssetTableInDialog';
import { assetExporter } from '../../utils/customExporter';
import { xgacDataProvider } from '../../dataProviders/xgacDataProvider';
import { RaXgacRecord } from '../../lib/RaXgacRecord';

type AssetsInDialogButtonProps = ({
  resource: 'asset-groups';
  hasExporter?: boolean;
  nameColumnLabel?: never;
} | {
  resource: 'certificates';
  nameColumnLabel: string;
  hasExporter?: never;
} | {
  resource: 'security-inventory-categories';
  nameColumnLabel: string;
  hasExporter?: never;
}) & {
  variant?: 'bhvk' | 'xgac';
  source: string;
  label: string;
  buttonLabel?: string;
  className?: string;
  closeClassName?: string;
};

export const AssetsInDialogButton = (props: AssetsInDialogButtonProps) => {

  const translationResource = props.resource === 'certificates' ? 'certification-categories' : props.resource;
  const translate = useTranslate();
  const redirect = useRedirect();
  const [dialogOpen, setDialogOpen] = useState(false);
  const record = useRecordContext();

  const assetIds = useMemo(() => {

    const rawItems = get(record, props.source) ?? [];
    return rawItems.map((item: { _id: string } | string) => {

      if (typeof item === 'string') {

        return item;

      }
      return item._id;

    });

  }, [record, props.source]);

  const emails = useMemo(
    () => (get(record, 'notifiedPeople.emails') ?? []) as string[],
    [record],
  );

  const isCertificationResource = props.resource === 'certificates';

  const hasCertifiedUser = isCertificationResource
    && record != null
    && Boolean(get(record, 'notifiedPeople.isCertificationHolderNotified') ?? true);

  const showResponsibles = props.resource === 'certificates' || props.resource === 'security-inventory-categories';
  const showEmailsInDialog = isCertificationResource || props.resource === 'security-inventory-categories';

  const totalCount = assetIds.length + emails.length + (hasCertifiedUser ? 1 : 0);

  const hasNoRecipients = showResponsibles && totalCount === 0;
  const needsSingleAssetPreview = totalCount === 1 && assetIds.length === 1;

  const shouldFetchAssets = assetIds.length > 0 && (dialogOpen || needsSingleAssetPreview);

  // load when opening a dialog or when there is only one responsible, whose name needs to be previewed in the button
  const { data: assets = [], isPending: assetsPending } = useGetManyAggregate<RaXgacRecord<Asset>>('all-assets', {
    ids: assetIds,
    meta: { sort: 'name' },
  }, {
    enabled: shouldFetchAssets,
  });

  const loading = shouldFetchAssets && assetsPending;

  let buttonLabel = translate(props.buttonLabel || 'general.text.show_persons', { length: totalCount });

  if (hasNoRecipients) {

    buttonLabel = translate('general.text.nobody_click_to_fix');

  } else if (totalCount > 1) {

    buttonLabel = translate(props.buttonLabel || 'general.text.show_persons', { length: totalCount });

  } else if (assetIds.length === 1 && assets[0]) {

    buttonLabel = assets[0].name;

  } else if (emails.length === 1) {

    buttonLabel = emails[0];

  } else if (hasCertifiedUser) {

    buttonLabel = translate('resources.certification-categories.fields.certified_user');

  }

  const isSingleEmailLabel = totalCount === 1 && emails.length === 1 && assetIds.length === 0;

  if (!record) return null;

  const openDialog = () => {

    setDialogOpen(true);

  };

  const closeDialog = () => {

    setDialogOpen(false);

  };

  const exportAssets = async () => {

    await assetExporter(assets, fetchRelatedRecords(xgacDataProvider as any as DataProvider));

  };

  const redirectToEdit = (options?: { focusNotifiedPeople?: boolean }) => {

    closeDialog();
    redirect(
      'edit',
      props.resource,
      record.id,
      undefined,
      options?.focusNotifiedPeople ? { focusNotifiedPeople: true } : {},
    );

  };

  const handleButtonClick = () => {

    if (totalCount === 0) {

      redirectToEdit({ focusNotifiedPeople: showResponsibles });
      return;

    }

    openDialog();

  };

  const addPersonButton = (
    <Button
      label="resources.asset-groups.text.add_person"
      startIcon={<AddIcon />}
      onClick={() => redirectToEdit({ focusNotifiedPeople: showResponsibles })}
      variant="contained"
      color="primary"
      size="medium"
    />
  );

  const responsiblesButtonSx = {
    display: 'flex',
    whiteSpace: 'nowrap',
    width: showResponsibles ? '100%' : undefined,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    '& .MuiButton-label': {
      flex: '1 1 auto',
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: 0,
      maxWidth: '100%',
    },
  } as const;

  const singleEmailButton = (
    <MuiButton
      variant="contained"
      color="primary"
      onClick={handleButtonClick}
      className={props.className}
      aria-label={emails[0]}
      sx={{
        ...responsiblesButtonSx,
        '& .AssetsInDialogButton-emailWrapper': {
          position: 'relative',
          display: 'block',
          width: '100%',
          minWidth: 0,
          overflow: 'hidden',
        },
        '& .AssetsInDialogButton-emailLabel': {
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
          maxWidth: '100%',
          color: 'inherit',
          filter: 'blur(4px)',
          opacity: 0.85,
          transition: 'filter 200ms, opacity 200ms',
        },
        '& .AssetsInDialogButton-hoverHint': {
          position: 'absolute',
          left: 0,
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          textAlign: 'center',
          pointerEvents: 'none',
          color: 'inherit',
          opacity: 1,
          transition: 'opacity 200ms',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        '&:hover .AssetsInDialogButton-hoverHint': {
          opacity: 0,
        },
        '&:hover .AssetsInDialogButton-emailLabel': {
          filter: 'none',
          opacity: 1,
        },
      }}
    >
      <span className="AssetsInDialogButton-emailWrapper">
        <span className="AssetsInDialogButton-emailLabel">{emails[0]}</span>
        <span className="AssetsInDialogButton-hoverHint">
          {translate('general.text.hover_to_view')}
        </span>
      </span>
    </MuiButton>
  );

  const responsiblesButton = (
    <Button
      variant={'contained'}
      disabled={needsSingleAssetPreview && loading}
      color={hasNoRecipients ? 'error' : 'primary'}
      onClick={handleButtonClick}
      label={buttonLabel}
      className={props.className}
      sx={responsiblesButtonSx}
    />
  );

  return (<>
    <Box sx={{
      width: showResponsibles ? '100%' : undefined,
      minWidth: 0,
      maxWidth: '100%',
      overflow: 'hidden',
    }}>
      {isSingleEmailLabel ? singleEmailButton : responsiblesButton}
    </Box>
    <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="lg" fullWidth>
      <DialogTitle className="flex-in-between" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {`${translate(`resources.${translationResource}.text.title_single`)} ${record.name}`}
        <IconButton onClick={closeDialog}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <AssetTableInDialog
        assets={assets}
        loading={loading}
        nameColumnLabel={props.nameColumnLabel}
        variant={props.variant}
        emails={showEmailsInDialog ? emails : undefined}
        showCertifiedUserRow={hasCertifiedUser}
        headerActions={addPersonButton}
      />
      <DialogActions>
        <Button
          onClick={closeDialog}
          label={translate('general.text.close')}
          {...(props.closeClassName
            ? {
              className: props.closeClassName,
              variant: 'contained' as const,
              size: 'medium' as const,
            }
            : {})}
        />
        {props.hasExporter && (
          <Button
            label="ra.action.export"
            startIcon={<FileDownloadIcon/>}
            onClick={exportAssets}
            variant="contained"
            sx={{ marginRight: '10px' }}
          />
        )}
      </DialogActions>
    </Dialog>
  </>);

};
